import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { ArrowLeft, ArrowRight, Check, Loader2, Sparkles, Wand2 } from "lucide-react";

export default function LOSScan() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tmpl, setTmpl] = useState(null);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0); // global question index
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [essayBusy, setEssayBusy] = useState(false);
  const [essayStats, setEssayStats] = useState(null);
  const [essayResult, setEssayResult] = useState(null);
  const [lifemapStatus, setLifemapStatus] = useState(null); // {owns, price_chf, ...}
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  // Auth guard
  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate("/auth/login?next=/los/scan");
  }, [user, authLoading, navigate]);

  // Load template + existing response
  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/los/template"),
      api.get("/los/response").catch(() => ({ data: { answers: {} } })),
      api.get("/los/lifemap/essays").catch(() => ({ data: { count: 0, sections: [] } })),
      api.get("/los/lifemap/status").catch(() => ({ data: null })),
    ]).then(([t, r, e, s]) => {
      setTmpl(t.data);
      setAnswers(r.data.answers || {});
      setSubmitted(!!r.data.completed);
      setEssayStats(e.data || { count: 0, sections: [] });
      setLifemapStatus(s.data);
      const flat = (t.data.sections || []).flatMap((s) => s.questions);
      const firstUnans = flat.findIndex((q) => !(q.id in (r.data.answers || {})));
      setIdx(firstUnans >= 0 ? firstUnans : 0);
    });
  }, [user]);

  // Build flat list of questions with section ref
  const flat = useMemo(() => {
    if (!tmpl) return [];
    return tmpl.sections.flatMap((s) => s.questions.map((q) => ({ ...q, section: s })));
  }, [tmpl]);

  if (authLoading || !user) return <div className="min-h-screen bg-[#081328] pt-32 text-center text-white/55">Lade…</div>;
  if (!tmpl) return <div className="min-h-screen bg-[#081328] pt-32 text-center text-white/55"><Loader2 className="animate-spin mx-auto text-[#D4AF37]" /></div>;

  const q = flat[idx];
  const total = flat.length;
  const answered = Object.keys(answers).filter((k) => answers[k] !== undefined && answers[k] !== "").length;
  const progress = Math.round((answered / total) * 100);
  const value = answers[q?.id] ?? (q?.kind === "multi" ? [] : "");
  const canNext = value !== "" && !(Array.isArray(value) && value.length === 0);

  const saveAnswer = async (val) => {
    setAnswers((a) => ({ ...a, [q.id]: val }));
    setSaving(true);
    setError(null);
    try {
      await api.post("/los/response/answer", { question_id: q.id, value: val });
      setSavedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const next = () => {
    if (idx < total - 1) setIdx(idx + 1);
  };
  const prev = () => { if (idx > 0) setIdx(idx - 1); };

  const submit = async () => {
    if (answered < total) {
      if (!window.confirm(`Du hast erst ${answered}/${total} Fragen beantwortet. Trotzdem einreichen?`)) return;
    }
    try {
      await api.post("/los/response/submit");
      setSubmitted(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Einreichen fehlgeschlagen");
    }
  };

  // Section number (1-based) — find which section this q belongs to
  const sectionIdx = tmpl.sections.findIndex((s) => s.key === q.section.key);
  const qIndexInSection = q.section.questions.findIndex((qq) => qq.id === q.id);

  return (
    <div className="min-h-screen bg-[#081328] text-[#F7F6F2]" data-testid="los-scan">
      {/* Progress bar at top */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-white/5">
        <div className="h-full transition-all duration-500"
             style={{ width: `${progress}%`, background: "linear-gradient(90deg,#D4AF37,#a48cff)" }}
             data-testid="los-progress" />
      </div>

      <div className="max-w-3xl mx-auto px-6 py-24">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-12">
          <Link to="/los" className="inline-flex items-center gap-2 text-white/55 hover:text-[#D4AF37] text-sm" data-testid="los-back">
            <ArrowLeft size={16} /> Zurück zur Übersicht
          </Link>
          <div className="text-xs text-white/55 tracking-wider">
            {answered}/{total} · {progress}%
            {saving && <span className="ml-2 text-[#a48cff]">speichert…</span>}
            {!saving && savedAt && <span className="ml-2 text-[#48d597]">gespeichert</span>}
          </div>
        </div>

        {/* Section banner */}
        <div className="mb-10" key={q.section.key + "-banner"}>
          <div className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: q.section.color }}>
            Raum {String(sectionIdx + 1).padStart(2, "0")} · {q.section.title}
          </div>
          <div className="text-white/45 italic text-sm">{q.section.intro}</div>
        </div>

        {/* Question */}
        <div key={q.id} className="los-fade-in">
          <div className="text-white/40 text-xs tracking-wider mb-4">
            Frage {qIndexInSection + 1} von {q.section.questions.length}
          </div>
          <h2 className="serif text-2xl md:text-4xl font-light leading-tight tracking-tight mb-10">
            {q.prompt}
          </h2>

          {/* Render input by kind */}
          {q.kind === "textarea" && (
            <textarea
              value={value}
              onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
              onBlur={(e) => e.target.value && saveAnswer(e.target.value)}
              placeholder={q.placeholder || "Schreib so ehrlich, wie du kannst…"}
              className="w-full min-h-[180px] bg-white/[0.03] border border-white/10 rounded-lg p-5 text-[15px] leading-relaxed text-white placeholder-white/30 focus:border-[#D4AF37] focus:outline-none transition"
              data-testid={`q-input-${q.id}`}
              autoFocus
            />
          )}

          {q.kind === "text" && (
            <input
              type="text"
              value={value}
              onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
              onBlur={(e) => e.target.value && saveAnswer(e.target.value)}
              placeholder={q.placeholder || ""}
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg p-5 text-[15px] text-white placeholder-white/30 focus:border-[#D4AF37] focus:outline-none transition"
              data-testid={`q-input-${q.id}`}
              autoFocus
            />
          )}

          {q.kind === "scale" && (
            <div className="space-y-4" data-testid={`q-scale-${q.id}`}>
              <div className="grid grid-cols-10 gap-2">
                {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                  <button
                    key={n}
                    onClick={() => saveAnswer(n)}
                    className={`aspect-square rounded-lg border text-base font-semibold transition ${
                      value === n
                        ? "bg-[#D4AF37] text-[#081328] border-[#D4AF37]"
                        : "bg-white/[0.02] border-white/10 text-white/70 hover:border-[#D4AF37]/50 hover:bg-white/[0.05]"
                    }`}
                  >{n}</button>
                ))}
              </div>
              <div className="flex justify-between text-xs text-white/40">
                <span>gar nicht</span>
                <span>vollkommen</span>
              </div>
            </div>
          )}

          {q.kind === "single" && (
            <div className="space-y-3" data-testid={`q-single-${q.id}`}>
              {q.options.map((opt) => (
                <button
                  key={opt}
                  onClick={() => saveAnswer(opt)}
                  className={`w-full text-left px-5 py-4 rounded-lg border transition ${
                    value === opt
                      ? "bg-[#D4AF37]/15 border-[#D4AF37] text-white"
                      : "bg-white/[0.02] border-white/10 text-white/70 hover:border-[#D4AF37]/40 hover:bg-white/[0.04]"
                  }`}
                >
                  <span className="inline-block w-3 h-3 rounded-full mr-3 align-middle border" style={{ borderColor: value === opt ? "#D4AF37" : "#ffffff30", background: value === opt ? "#D4AF37" : "transparent" }} />
                  {opt}
                </button>
              ))}
            </div>
          )}

          {q.kind === "multi" && (
            <div className="space-y-3" data-testid={`q-multi-${q.id}`}>
              <div className="text-xs text-white/45 mb-2">Mehrfachauswahl möglich</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {q.options.map((opt) => {
                  const checked = Array.isArray(value) && value.includes(opt);
                  return (
                    <button
                      key={opt}
                      onClick={() => {
                        const arr = Array.isArray(value) ? [...value] : [];
                        if (arr.includes(opt)) saveAnswer(arr.filter((x) => x !== opt));
                        else saveAnswer([...arr, opt]);
                      }}
                      className={`text-left px-4 py-3 rounded-lg border text-sm transition ${
                        checked
                          ? "bg-[#D4AF37]/15 border-[#D4AF37] text-white"
                          : "bg-white/[0.02] border-white/10 text-white/70 hover:border-[#D4AF37]/40"
                      }`}
                    >
                      <span className="inline-block w-3.5 h-3.5 rounded-sm mr-2 align-middle border" style={{ borderColor: checked ? "#D4AF37" : "#ffffff30", background: checked ? "#D4AF37" : "transparent" }} />
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {error && <div className="mt-4 text-sm text-red-400">{error}</div>}
        </div>

        {/* Navigation */}
        <div className="mt-14 flex items-center justify-between">
          <button onClick={prev} disabled={idx === 0}
            className="inline-flex items-center gap-2 px-5 py-3 text-sm text-white/65 hover:text-white disabled:opacity-30"
            data-testid="los-prev">
            <ArrowLeft size={14} /> Zurück
          </button>
          {idx < total - 1 ? (
            <button onClick={next}
              className="inline-flex items-center gap-2 px-7 py-3 bg-[#D4AF37] text-[#081328] font-semibold rounded-full hover:bg-[#e3c252] transition disabled:opacity-40"
              data-testid="los-next" disabled={!canNext}>
              Weiter <ArrowRight size={14} />
            </button>
          ) : (
            <button onClick={submit}
              className="inline-flex items-center gap-2 px-7 py-3 bg-[#48d597] text-[#081328] font-semibold rounded-full hover:bg-[#5cdfa3] transition"
              data-testid="los-submit">
              <Check size={14} /> Scan abschließen
            </button>
          )}
        </div>

        {submitted && (
          <div className="mt-12 border border-[#48d597]/40 bg-[#48d597]/5 rounded-xl p-8 text-center" data-testid="los-submitted">
            <Sparkles size={28} className="mx-auto text-[#48d597] mb-3" />
            <h3 className="serif text-2xl mb-2">Dein Scan ist gespeichert.</h3>
            <p className="text-white/55 text-sm">Nenad wird sich in den nächsten Tagen mit deiner ersten Reflexion melden.</p>
          </div>
        )}

        {/* Life Map PDF — visible once user has ≥1 answer */}
        {Object.keys(answers).length > 0 && (
          <div className="mt-8 border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/10 to-[#a48cff]/5 rounded-2xl p-8 md:p-10 text-center" data-testid="lifemap-download-card">
            <div className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-3">
              SAT Lebenslandkarte · {lifemapStatus?.owns ? "Deine Karte" : "Vorschau"}
            </div>
            <h3 className="serif text-3xl font-light text-white mb-3">
              {lifemapStatus?.owns ? "Deine Karte ist bereit." : "Schau einen ersten Raum an."}
            </h3>
            <p className="text-white/65 text-sm max-w-lg mx-auto mb-6 leading-relaxed">
              {lifemapStatus?.owns
                ? "Lade dein vollständiges 12-Räume-Dokument herunter — alle Antworten, alle Reflexionen, lebenslange Updates."
                : "Die kostenlose Vorschau zeigt dir Cover, Vorwort und den ersten Raum (Wurzeln & Kindheit) — mit deiner persönlichen KI-Reflexion. Wenn dich das berührt, kannst du die vollen 12 Räume freischalten."}
            </p>

            {/* Essay generation — only when owned, or as teaser if not */}
            {lifemapStatus?.owns ? (
              <div className="mb-6 max-w-lg mx-auto">
                {essayStats && essayStats.count > 0 ? (
                  <div className="text-xs text-[#48d597] flex items-center justify-center gap-2 mb-3" data-testid="essays-status">
                    <Check size={12} /> {essayStats.count} personalisierte Reflexionen sind eingebettet
                  </div>
                ) : (
                  <div className="text-xs text-white/45 italic mb-3">
                    Tipp: Erschaffe personalisierte Reflexionen zu jedem Raum.
                  </div>
                )}
                <button
                  disabled={essayBusy}
                  onClick={async () => {
                    setEssayBusy(true); setEssayResult(null);
                    try {
                      const r = await api.post("/los/lifemap/essays/generate", null, { timeout: 180000 });
                      setEssayResult(r.data);
                      const e = await api.get("/los/lifemap/essays");
                      setEssayStats(e.data);
                    } catch (e) {
                      setEssayResult({ error: e?.response?.data?.detail || "Generierung fehlgeschlagen" });
                    } finally { setEssayBusy(false); }
                  }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#a48cff]/55 text-[#cdb8ff] rounded-full text-xs hover:bg-[#a48cff]/10 transition disabled:opacity-60 disabled:cursor-wait"
                  data-testid="essays-generate-btn"
                >
                  {essayBusy ? (<><Loader2 size={12} className="animate-spin" /> KI-Reflexionen werden erschaffen (~60s)…</>) :
                    essayStats && essayStats.count > 0 ? (<><Wand2 size={12} /> Reflexionen aktualisieren</>) :
                    (<><Wand2 size={12} /> Personalisierte Reflexionen erschaffen</>)}
                </button>
                {essayResult && !essayResult.error && (
                  <div className="text-[10px] text-white/50 italic mt-2">
                    {essayResult.generated} neu · {essayResult.skipped} im Cache · {essayResult.failed} fehlgeschlagen
                  </div>
                )}
                {essayResult && essayResult.error && (
                  <div className="text-[10px] text-red-300 mt-2">{essayResult.error}</div>
                )}
              </div>
            ) : (
              <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto text-left">
                {[
                  "Alle 12 Räume mit deinen Antworten",
                  "12 KI-Reflexionen — je ~2 Seiten",
                  "60 Integrations-Fragen",
                  "Lifetime-Updates · für immer",
                ].map((b) => (
                  <div key={b} className="flex items-start gap-2 text-xs text-white/70">
                    <Check size={12} className="text-[#48d597] mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-3">
              {/* Preview / Download */}
              <button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem("sat_token");
                    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/los/lifemap/pdf`, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    if (!r.ok) throw new Error("Download fehlgeschlagen");
                    const blob = await r.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = lifemapStatus?.owns ? "sat-lebenslandkarte.pdf" : "sat-lebenslandkarte-vorschau.pdf";
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (_) { /* noop */ }
                }}
                className={`inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold text-sm transition ${
                  lifemapStatus?.owns
                    ? "bg-[#D4AF37] text-[#0E1A30] hover:opacity-90"
                    : "border border-white/20 text-white hover:border-[#D4AF37]/60"
                }`}
                data-testid="lifemap-download-btn"
              >
                <Sparkles size={14} /> {lifemapStatus?.owns ? "Vollständige Karte (PDF)" : "Vorschau lesen (gratis)"}
              </button>

              {/* Purchase CTA */}
              {!lifemapStatus?.owns && (
                <button
                  disabled={checkoutBusy}
                  onClick={async () => {
                    setCheckoutBusy(true);
                    try {
                      const r = await api.post("/payment/checkout", {
                        product_key: "lifemap_full",
                        origin_url: window.location.origin,
                      });
                      if (r.data?.url) {
                        window.location.href = r.data.url;
                      }
                    } catch (e) {
                      console.error("checkout failed", e);
                      setCheckoutBusy(false);
                    }
                  }}
                  className="inline-flex items-center gap-2 px-7 py-3 bg-[#D4AF37] text-[#0E1A30] rounded-full font-semibold text-sm hover:opacity-90 transition disabled:opacity-60"
                  data-testid="lifemap-buy-btn"
                >
                  {checkoutBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  Volle Karte freischalten · CHF {lifemapStatus?.price_chf || 79}
                </button>
              )}
            </div>

            <div className="text-[10px] text-white/45 mt-4 italic">
              {lifemapStatus?.owns
                ? `~${(() => {
                    const base = Math.round(Object.keys(answers).length * 0.8);
                    const withEssays = (essayStats?.count || 0) * 2;
                    return Math.max(20, Math.min(150, base + withEssays));
                  })()} Seiten · individuell aus deinen Antworten geschaffen`
                : "Sichere Bezahlung via Stripe · einmalige Investition · Lifetime-Zugang"}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .los-fade-in { animation: losFadeIn 0.6s ease-out; }
        @keyframes losFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
